import React from 'react';

type ContextMap = Record<string, React.Context<any>>;

type WithContexts<P, CtxMap extends ContextMap> = P & {
  [K in keyof CtxMap]: React.ContextType<CtxMap[K]>;
};

export function withContexts<P, CtxMap extends ContextMap>(
  WrappedComponent: React.ComponentType<WithContexts<P, CtxMap>>,
  contexts: CtxMap,
): React.FC<Omit<P, keyof CtxMap>> {
  const contextKeys = Object.keys(contexts) as (keyof CtxMap)[];

  function buildNestedContext(
    keys: (keyof CtxMap)[],
    props: any,
    finalRender: (injectedProps: any) => React.ReactNode,
  ): React.ReactNode {
    if (keys.length === 0) return finalRender(props);

    const [currentKey, ...rest] = keys;
    const CurrentContext = contexts[currentKey];

    return (
      <CurrentContext.Consumer>
        {(value) => {
          const updatedProps = { ...props, [currentKey]: value };
          return buildNestedContext(rest, updatedProps, finalRender);
        }}
      </CurrentContext.Consumer>
    );
  }

  return function ContextWrapped(props: Omit<P, keyof CtxMap>) {
    return (
      <>
        {buildNestedContext(contextKeys, props, (finalProps) => (
          <WrappedComponent {...(finalProps as WithContexts<P, CtxMap>)} />
        ))}
      </>
    );
  };
}
